import React, { useState } from 'react';
import PageHero from './PageHero';
import './Insights.css';

const Insights = () => {
  const [selectedInsight, setSelectedInsight] = useState(null);

  const insights = [
    {
      id: 1,
      title: 'Generative AI (GenAI)',
      category: 'Artificial Intelligence',
      icon: 'fas fa-brain',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
      gradient: 'linear-gradient(135deg, #667eea, #764ba2)',
      what: 'Generative AI refers to artificial intelligence systems capable of creating new content, including text, images, code, and multimedia. Leveraging large language models (LLMs) and deep learning, GenAI can generate human-like outputs, automate creative processes, and provide intelligent assistance across various domains.',
      why: 'Organizations adopting GenAI can revolutionize productivity, automate repetitive tasks, enhance decision-making, and create personalized customer experiences. With the ability to process vast amounts of data and generate insights, GenAI drives innovation while reducing operational costs and time-to-market for new products and services.',
      how: 'Rivontrix helps organizations implement GenAI through strategic assessment, custom LLM integration, prompt engineering, fine-tuning models for domain-specific use cases, and building secure GenAI workflows. We ensure responsible AI deployment with proper governance, ethical frameworks, and security measures.',
      useCases: [
        'Automated content generation and documentation',
        'Intelligent chatbots and virtual assistants',
        'Code generation and software development acceleration',
        'Data analysis and business intelligence',
        'Personalized customer experiences'
      ]
    },
    {
      id: 2,
      title: 'GenAI Frameworks',
      category: 'Technology Framework',
      icon: 'fas fa-project-diagram',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80',
      gradient: 'linear-gradient(135deg, #f093fb, #f5576c)',
      what: 'GenAI frameworks provide structured approaches to developing, deploying, and managing generative AI applications. These include frameworks like LangChain, LlamaIndex, Hugging Face Transformers, and custom enterprise solutions that enable rapid development, integration, and scaling of GenAI capabilities.',
      why: 'Using the right GenAI framework accelerates development, ensures scalability, provides necessary abstractions for complex AI operations, and enables integration with existing enterprise systems. Frameworks reduce complexity, standardize processes, and provide built-in best practices for security, performance, and compliance.',
      how: 'Rivontrix evaluates your requirements and recommends the optimal GenAI framework. We provide implementation, custom framework development, integration with your tech stack, training, and ongoing support to ensure successful adoption and ROI.',
      useCases: [
        'RAG (Retrieval-Augmented Generation) implementation',
        'Multi-LLM orchestration and routing',
        'Vector database integration',
        'Enterprise GenAI platform development',
        'AI agent frameworks and automation'
      ]
    },
    {
      id: 3,
      title: 'Quantum Computing Threats & Defense',
      category: 'Cybersecurity',
      icon: 'fas fa-atom',
      image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=800&q=80',
      gradient: 'linear-gradient(135deg, #4facfe, #00f2fe)',
      what: 'Quantum computing poses a significant threat to current cryptographic standards. Quantum computers can break traditional encryption algorithms (like RSA and ECC) that protect sensitive data today. This quantum threat requires organizations to adopt quantum-resistant cryptography and prepare for the post-quantum era.',
      why: 'Current encryption protecting your data, communications, and digital transactions will become vulnerable when quantum computers achieve sufficient scale. Organizations must act now to implement quantum-resistant algorithms, upgrade security infrastructure, and protect against "harvest now, decrypt later" attacks.',
      how: 'Rivontrix offers quantum-ready security assessments, migration to post-quantum cryptographic standards (NIST-approved algorithms), hybrid cryptography solutions, and quantum-safe network infrastructure. We help organizations transition smoothly while maintaining security during the migration period.',
      useCases: [
        'Post-quantum cryptography implementation',
        'Quantum-safe network and cloud infrastructure',
        'Crypto-agility framework development',
        'Quantum threat risk assessment',
        'Secure migration planning and execution'
      ]
    },
    {
      id: 4,
      title: 'Robust Data Engineering & Pipelines',
      category: 'Data Infrastructure',
      icon: 'fas fa-database',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
      gradient: 'linear-gradient(135deg, #fa709a, #fee140)',
      what: 'Robust data engineering involves building reliable, scalable, and efficient data pipelines that extract, transform, and load (ETL) data from various sources. Quality data pipelines ensure data integrity, real-time processing, error handling, monitoring, and governance throughout the data lifecycle.',
      why: 'High-quality data pipelines are fundamental to data-driven decision making, AI/ML model accuracy, real-time analytics, and business intelligence. Poor data quality leads to unreliable insights, compliance risks, and costly errors. Robust pipelines ensure data availability, consistency, and trustworthiness.',
      how: 'Rivontrix designs and implements enterprise-grade data pipelines using modern technologies (Apache Airflow, Kafka, Spark), implements data quality frameworks, establishes monitoring and alerting, ensures scalability with cloud-native architectures, and provides data governance best practices.',
      useCases: [
        'Real-time data streaming and processing',
        'Data warehouse and lake architectures',
        'ETL/ELT pipeline development',
        'Data quality and governance frameworks',
        'Event-driven data architectures'
      ]
    },
    {
      id: 5,
      title: 'Data & AI Integration',
      category: 'AI Strategy',
      icon: 'fas fa-chart-line',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
      gradient: 'linear-gradient(135deg, #30cfd0, #330867)',
      what: 'Data and AI integration involves combining high-quality data infrastructure with artificial intelligence capabilities to create intelligent systems that learn, adapt, and provide actionable insights. This includes MLOps, data science workflows, feature engineering, model deployment, and continuous learning systems.',
      why: 'True AI value comes from seamless integration between data and AI systems. Organizations need unified platforms where data flows efficiently into AI models, models provide feedback to improve data quality, and insights drive business outcomes. This integration eliminates silos and accelerates time-to-value for AI initiatives.',
      how: 'Rivontrix builds integrated Data-AI platforms combining modern data stacks with MLOps, implements automated model training and deployment pipelines, creates feature stores, establishes data science workflows, and ensures continuous model monitoring and retraining.',
      useCases: [
        'MLOps pipeline development',
        'Feature store and data science platforms',
        'Automated model training and deployment',
        'AI-powered data quality and governance',
        'Real-time inference and prediction systems'
      ]
    }
  ];

  const closeModal = () => {
    setSelectedInsight(null);
    document.body.style.overflow = 'auto';
  };

  const openModal = (insight) => {
    setSelectedInsight(insight);
    document.body.style.overflow = 'hidden';
  };

  return (
    <>
      <PageHero
        sectionId="insights"
        title="Insights"
        subtitle="Thought Leadership"
        description="Explore our latest thought leadership, ideas, and insights on the technologies shaping the future of business and society."
        image="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=1600&q=80"
        gradient="linear-gradient(135deg, rgba(102, 126, 234, 0.9), rgba(118, 75, 162, 0.9))"
      />
      <section id="insights" className="insights">
        <div className="container">
          <div className="insights-grid">
            {insights.map((insight) => (
              <div
                key={insight.id}
                className="insight-card"
                onClick={() => openModal(insight)}
                style={{ cursor: 'pointer' }}
              >
                <div className="insight-image-wrapper">
                  <div 
                    className="insight-image" 
                    style={{ backgroundImage: `url(${insight.image})` }}
                  >
                    <div className="insight-image-overlay" style={{ background: insight.gradient }}></div>
                  </div>
                </div>
                <div className="insight-card-content">
                  <div className="insight-icon" style={{ background: insight.gradient }}>
                    <i className={insight.icon}></i>
                  </div>
                  <h3>{insight.title}</h3>
                  <p className="insight-category-badge">{insight.category}</p>
                  <div className="insight-cta">
                    Click to Explore
                    <i className="fas fa-arrow-right"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="insights-cta">
            <div className="cta-content">
              <h3>Want to Dive Deeper?</h3>
              <p>
                Connect with our experts to discuss how these insights can transform your organization.
                Let's explore how Rivontrix can help you leverage cutting-edge technologies.
              </p>
              <a 
                href="#contact" 
                className="btn btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  const event = new CustomEvent('sectionChange', { detail: 'contact' });
                  window.dispatchEvent(event);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Get Expert Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {selectedInsight && (
        <div className="insight-modal-overlay" onClick={closeModal}>
          <div className="insight-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <i className="fas fa-times"></i>
            </button>

            <div className="modal-header">
              <div
                className="modal-image"
                style={{ backgroundImage: `url(${selectedInsight.image})` }}
              >
                <div className="modal-image-overlay" style={{ background: selectedInsight.gradient }}></div>
              </div>
              <div className="modal-title-section">
                <div className="modal-icon" style={{background: selectedInsight.gradient}}>
                  <i className={selectedInsight.icon}></i>
                </div>
                <h2>{selectedInsight.title}</h2>
                <p className="modal-subtitle">{selectedInsight.category}</p>
              </div>
            </div>

            <div className="modal-content">
              <div className="modal-section">
                <h3>
                  <i className="fas fa-question-circle"></i>
                  What
                </h3>
                <p>{selectedInsight.what}</p>
              </div>

              <div className="modal-section">
                <h3>
                  <i className="fas fa-lightbulb"></i>
                  Why
                </h3>
                <p>{selectedInsight.why}</p>
              </div>

              <div className="modal-section">
                <h3>
                  <i className="fas fa-rocket"></i>
                  How
                </h3>
                <p>{selectedInsight.how}</p>
              </div>

              <div className="modal-section">
                <h3>
                  <i className="fas fa-list-check"></i>
                  Key Use Cases
                </h3>
                <ul className="use-cases-list">
                  {selectedInsight.useCases.map((useCase, idx) => (
                    <li key={idx}>
                      <i className="fas fa-check-circle"></i>
                      {useCase}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="modal-footer">
              <a 
                href="#contact" 
                className="btn btn-primary" 
                onClick={(e) => {
                  e.preventDefault();
                  closeModal();
                  const event = new CustomEvent('sectionChange', { detail: 'contact' });
                  window.dispatchEvent(event);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Get Expert Consultation
                <i className="fas fa-arrow-right"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Insights;

